import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import { Pill } from "@/components/Music";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

/** A small tag for writers who aren't signed to you (co-writers signed elsewhere). */
export function SignedTag({ w, publisher }) {
  if (w.signed) return null;
  return <Pill className="align-middle">Co-writer</Pill>;
}

/**
 * "Which writers do you represent?" — one checklist for the whole roster.
 * Ticked writers are paid from your royalties; everyone else is a co-writer
 * whose share is paid by their own publisher.
 */
export function SignedReview({ open, onOpenChange, publisher, onSaved }) {
  const [rows, setRows] = useState(null);
  const [ticked, setTicked] = useState(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setRows(null);
    setQ("");
    api
      .get("/writers/signed-review")
      .then(({ data }) => {
        setRows(data.writers);
        setTicked(
          new Set(data.writers.filter((w) => w.signed).map((w) => w.writer_id)),
        );
      })
      .catch((e) => toast.error(apiError(e)));
  }, [open]);
  const shown = useMemo(
    () =>
      (rows || []).filter(
        (w) =>
          !q ||
          `${w.name} ${w.ipi || ""}`.toLowerCase().includes(q.toLowerCase()),
      ),
    [rows, q],
  );
  const toggle = (id) =>
    setTicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/writers/signed", {
        signed_ids: [...ticked],
      });
      toast.success(
        `${data.signed} writer${data.signed === 1 ? "" : "s"} signed to ${publisher || "you"} — payouts updated`,
      );
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[88vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            Which writers are signed to {publisher || "you"}?
          </DialogTitle>
        </DialogHeader>
        <p className="text-[13px] text-muted2 -mt-1">
          What the MLC and BMI pay you is your writers' share only. Ticked
          writers are paid from it, split by their shares on each song.
          Co-writers stay on your tracks for registration but are paid by their
          own publisher.
        </p>
        <label className="relative block">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a writer…"
            aria-label="Find a writer"
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring"
          />
        </label>
        <div className="flex-1 min-h-[200px] overflow-y-auto -mx-1 px-1">
          {!rows ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-muted2" />
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {shown.map((w) => {
                const on = ticked.has(w.writer_id);
                return (
                  <li key={w.writer_id}>
                    <label
                      className={cn(
                        "flex items-start gap-3 py-2.5 px-2 rounded-lg cursor-pointer hover:bg-surface",
                        on && "bg-brand-light/40",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(w.writer_id)}
                        className="mt-1 w-4 h-4 accent-[hsl(var(--brand))]"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13.5px] font-semibold">
                          {w.name}{" "}
                          {w.active === false && (
                            <span className="text-[11.5px] font-normal text-muted2">
                              · inactive
                            </span>
                          )}
                        </span>
                        <span className="block text-[11.5px] text-muted2">
                          {[
                            w.pro,
                            w.ipi ? `IPI ${w.ipi}` : "",
                            `${w.credits} credit${w.credits === 1 ? "" : "s"}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                        {w.why?.length > 0 && (
                          <span className="block text-[11.5px] text-brand">
                            Likely yours: {w.why.join(", ")}
                          </span>
                        )}
                      </span>
                      <span className="text-[11.5px] font-semibold whitespace-nowrap pt-0.5">
                        {on ? (
                          "Signed"
                        ) : (
                          <span className="text-muted2 font-normal">
                            Co-writer
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <DialogFooter className="items-center gap-2 sm:justify-between">
          <span className="text-[12.5px] text-muted2">
            {ticked.size} of {rows?.length ?? "…"} signed
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={busy || !rows}
              className="bg-brand hover:bg-brand-hover"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
